import type { Session, User } from '@supabase/supabase-js'
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { isSupabaseConfigured, supabase } from '../supabase/client'

interface AuthState { session: Session | null; user: User | null; loading: boolean; configured: boolean; signIn(email: string, password: string): Promise<void>; signOut(): Promise<void> }
const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(isSupabaseConfigured)
  useEffect(() => {
    if (!supabase) return
    let active = true
    const timeout = window.setTimeout(() => { if (active) setLoading(false) }, 8_000)
    void supabase.auth.getSession()
      .then(({ data }) => { if (active) setSession(data.session) })
      .catch(() => undefined)
      .finally(() => { if (active) { window.clearTimeout(timeout); setLoading(false) } })
    const { data } = supabase.auth.onAuthStateChange((_event, next) => { if (active) { setSession(next); setLoading(false) } })
    return () => { active = false; window.clearTimeout(timeout); data.subscription.unsubscribe() }
  }, [])
  const value = useMemo<AuthState>(() => ({
    session, user: session?.user ?? null, loading, configured: isSupabaseConfigured,
    async signIn(email, password) { if (!supabase) throw new Error('Supabase is not configured.'); const { error } = await supabase.auth.signInWithPassword({ email, password }); if (error) throw new Error(error.message) },
    async signOut() { if (!supabase) return; const { error } = await supabase.auth.signOut(); if (error) throw new Error(error.message) },
  }), [session, loading])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState { const value = useContext(AuthContext); if (!value) throw new Error('useAuth must be used inside AuthProvider'); return value }
